/**
 * `returnUrl` del login (SAAS-1862, B-8f): cuando la sesión no existe o expiró, se guarda
 * el destino al que se quería ir para volver ahí tras autenticarse (p. ej. el clic en frío
 * de una notificación push abre el deep link). Solo rutas internas del mismo origen.
 */

export const RETURN_URL_KEY = 'login_return_url';

const MAX_LENGTH = 2048;
// Páginas a las que volver no tiene sentido: el login mismo y la raíz del locale.
const NON_RETURNABLE =
  /^\/(?:[a-z]{2}(?:-[A-Za-z]+)?\/?)?$|^\/(?:[a-z]{2}(?:-[A-Za-z]+)?\/)?auth(?:\/|$)/;

/**
 * Devuelve `path + query` si `raw` es una ruta interna segura, o `null`. Rechaza esquemas,
 * `//host`, backslashes, caracteres de control y las páginas de auth. Si la app se sirve bajo
 * un `basePath`, se lo quita (el router de Next lo vuelve a agregar).
 */
export const sanitizeReturnUrl = (
  raw: unknown,
  basePath: string = ''
): string | null => {
  if (typeof raw !== 'string') return null;
  const value = raw.trim();
  if (!value || value.length > MAX_LENGTH) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\\]/.test(value)) return null;
  if (!value.startsWith('/') || value.startsWith('//')) return null;

  let parsed: URL;
  try {
    parsed = new URL(value, 'http://return-url.invalid');
  } catch {
    return null;
  }
  if (parsed.origin !== 'http://return-url.invalid') return null;

  let path = parsed.pathname;
  if (basePath && (path === basePath || path.startsWith(`${basePath}/`))) {
    path = path.slice(basePath.length) || '/';
  }
  if (path.startsWith('//') || NON_RETURNABLE.test(path)) return null;
  return `${path}${parsed.search}`;
};

const getBasePath = () => process.env.NEXT_PUBLIC_BASE_PATH || '';

/** Guarda el destino actual (path + query) para después del login. Best effort. */
export const saveReturnUrl = (): void => {
  if (typeof window === 'undefined') return;
  const url = sanitizeReturnUrl(
    `${window.location.pathname}${window.location.search}`,
    getBasePath()
  );
  try {
    if (url) window.sessionStorage.setItem(RETURN_URL_KEY, url);
    else window.sessionStorage.removeItem(RETURN_URL_KEY);
  } catch {
    // Sin sessionStorage: se pierde el destino, como antes.
  }
};

export const clearReturnUrl = (): void => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(RETURN_URL_KEY);
  } catch {
    // noop
  }
};

/** Lee y borra el destino guardado (re-validado), o `null` si no hay. */
export const consumeReturnUrl = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.sessionStorage.getItem(RETURN_URL_KEY);
    window.sessionStorage.removeItem(RETURN_URL_KEY);
    return sanitizeReturnUrl(stored, getBasePath());
  } catch {
    return null;
  }
};
